import request from '../utils/request';
import type {
  Project,
  ProjectGroup,
  ProjectListParams,
  ProjectListResponse,
  ProjectGroupListResponse,
  CreateProjectParams,
  CreateProjectGroupParams
} from '../types/project';
import { DataValidator } from '../utils/dataValidation';
import { ErrorHandler } from '../utils/errorHandler';

// 项目相关API
export const getProjectList = (params: ProjectListParams = {}) => {
  return request.get<ProjectListResponse>('/api/v1/project/projects/', { params });
};

export const getProjectDetail = (id: number) => {
  return request.get<Project>(`/api/v1/project/projects/${id}/`);
};

export const createProject = (data: CreateProjectParams) => {
  return request.post<Project>('/api/v1/project/projects/', data);
};

export const updateProject = (id: number, data: Partial<CreateProjectParams>) => {
  return request.put<Project>(`/api/v1/project/projects/${id}/`, data);
};

export const deleteProject = (id: number) => {
  return request.delete(`/api/v1/project/projects/${id}/`);
};

// 项目组相关API
export const getProjectGroupList = (params: {
  page: number;
  page_size: number;
  search?: string;
  ordering?: string;
  /** 仅查看该客户主账号作为 owner 的项目组（与后端 DjangoFilter 一致） */
  owner?: number;
}) => {
  return request.get<ProjectGroupListResponse>('/api/v1/project/project-groups/', { params });
};

export const getProjectGroupDetail = (id: number) => {
  return request.get<ProjectGroup>(`/api/v1/project/project-groups/${id}/`);
};

export const createProjectGroup = (data: CreateProjectGroupParams) => {
  return request.post<ProjectGroup>('/api/v1/project/project-groups/', data);
};

export const updateProjectGroup = (id: number, data: Partial<CreateProjectGroupParams>) => {
  return request.put<ProjectGroup>(`/api/v1/project/projects/${id}/`, data);
};

export const deleteProjectGroup = (id: number) => {
  return request.delete(`/api/v1/project/project-groups/${id}/`);
};

// 获取当前用户的项目列表（用于入库记录过滤）
export const getCurrentUserProjects = async (): Promise<Project[]> => {
  try {
    console.log('🔄 开始获取当前用户的项目列表...');
    
    let allProjects: Project[] = [];
    let currentPage = 1;
    let hasMore = true;
    
    // 分页获取所有项目，确保不遗漏任何项目
    while (hasMore) {
      const response = await request.get('/api/v1/project/projects/', {
        params: { 
          page: currentPage,
          page_size: 1000, // 每页1000条
          ordering: 'project_code' // 按项目编码排序，便于查找
        }
      });
      
      console.log(`📡 项目API响应 (第${currentPage}页):`, response.data);
      
      // 使用统一的数据验证工具
      const validatedData = DataValidator.validatePaginatedResponse<Project>(response.data);
      
      // 添加当前页的项目到总列表
      allProjects = [...allProjects, ...validatedData.results];
      
      // 检查是否还有更多页
      hasMore = validatedData.results.length === 1000 && validatedData.count > allProjects.length;
      currentPage++;
      
      console.log(`📄 第${currentPage - 1}页获取完成，当前总数: ${allProjects.length}，API返回总数: ${validatedData.count}`);
      
      // 安全检查：防止无限循环
      if (currentPage > 50) { // 最多50页，即50000个项目
        console.warn('⚠️ 达到最大页数限制，停止获取');
        break;
      }
    }
    
    console.log(`✅ 获取项目列表成功，共 ${allProjects.length} 个项目`);
    console.log('📋 项目列表预览:', allProjects.slice(0, 5).map(p => `${p.project_code} - ${p.name}`));
    
    // 项目数据获取完成
    return allProjects;
  } catch (error) {
    console.error('❌ 获取项目列表失败:', error);
    ErrorHandler.handleApiError(error, '获取项目数据');
    throw error;
  }
};

// 获取客户列表（用于筛选）
export const getCustomerList = async (options?: { onlyMain?: boolean }) => {
  try {
    console.log('🔄 开始获取客户列表...');
    // 分页拉取全部客户，兼容 DRF next 结构
    let url: string | null = '/api/v1/users/';
    let page = 1;
    const all: Array<{ id: string; username: string; name?: string; customer_code?: string }> = [];
    while (url) {
      const firstPageParams: any = { role: 'customer', page: 1, page_size: 200 };
      if (options?.onlyMain) {
        firstPageParams.sub_role = 'main_account';
      }
      const resp = await request.get(url, {
        params: page === 1 ? firstPageParams : undefined,
      });
      const data = resp.data;
      const items = Array.isArray(data)
        ? data
        : (Array.isArray(data?.results) ? data.results : []);
      items.forEach((u: any) => {
        if (u && typeof u.id !== 'undefined') {
          all.push({
            id: String(u.id),
            username: u.username,
            name: u.profile?.company_name || u.first_name || u.last_name ? `${u.first_name || ''}${u.last_name || ''}`.trim() : u.username,
            customer_code: u.profile?.customer_code || u.customer_code || u.code,
          });
        }
      });
      url = data?.next || null;
      page += 1;
      if (page > 200) break; // 保护
    }
    console.log('✅ 客户列表处理完成，总数:', all.length);
    return all;
  } catch (error) {
    console.error('❌ 获取客户列表失败:', error);
    // 如果API不存在，返回空数组
    return [];
  }
}; 